import { computed } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useAccountStore } from 'src/stores/account'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { log } from 'src/services/app/log.service'

export function useCompaniesDirectory() {
  const accountStore = useAccountStore()
  const queryClient = useQueryClient()

  const companies = computed(() => accountStore.accountData?.companies || {})

  function getCompany(companyId) {
    return companies.value[companyId] || null
  }

  function getWebsite(companyId) {
    const company = getCompany(companyId)
    return company?.website || null
  }

  function getProduct(companyId, productId) {
    const company = getCompany(companyId)
    if (!company || !company.listOfProducts) return null
    return company.listOfProducts[productId] || null
  }

  function getAllProducts(companyId) {
    const company = getCompany(companyId)
    if (!company || !company.listOfProducts) return []
    return Object.values(company.listOfProducts)
  }

  function getCompanyName({ companyId, ticketId }) {
    const company = getCompany(companyId)
    if (company) {
      return company.companyName || 'Unknown Company'
    }
    const tickets = accountStore.accountData?.tickets || []
    return tickets.find((ticket) => ticket.ticketId === ticketId)?.companyName || 'Unknown Company'
  }

  function getProductName({ companyId, productId, ticketId }) {
    const product = getProduct(companyId, productId)
    if (product) {
      return product.productName || 'Unknown Product'
    }
    const tickets = accountStore.accountData?.tickets || []
    return tickets.find((ticket) => ticket.ticketId === ticketId)?.productName || 'Unknown Product'
  }

  function resolveCompanyName({ companyId, fallbackName }) {
    const company = getCompany(companyId)
    if (company?.companyName) return company.companyName
    return fallbackName || 'Unknown Company'
  }

  function resolveProductName({ companyId, productId, fallbackName }) {
    const product = getProduct(companyId, productId)
    if (product?.productName) return product.productName
    return fallbackName || 'Unknown Product'
  }

  function addCompaniesToDirectory(companiesToAdd) {
    const accountData = queryClient.getQueryData([QUERY_KEYS.ACCOUNT_DATA])
    if (!accountData) {
      log.error('Cannot update directory: account data not found')
      return
    }

    const existingCompanies = accountData.companies || {}
    const updatedCompanies = { ...existingCompanies }

    // Merge fresh companies/products from backend with existing directory
    // Fresh data overrides stale local data to keep directory up-to-date
    Object.keys(companiesToAdd).forEach((companyId) => {
      updatedCompanies[companyId] = {
        ...updatedCompanies[companyId],
        ...companiesToAdd[companyId],
        listOfProducts: {
          ...(updatedCompanies[companyId]?.listOfProducts || {}),
          ...(companiesToAdd[companyId]?.listOfProducts || {}),
        },
      }
    })

    queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], {
      ...accountData,
      companies: updatedCompanies,
    })
    log.success('Companies directory updated')
  }

  return {
    companies,

    getCompany,
    getProduct,
    getWebsite,
    getCompanyName,
    getProductName,
    resolveCompanyName,
    resolveProductName,
    getAllProducts,
    addCompaniesToDirectory,
  }
}
